import { useState, useEffect, useCallback, useRef } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ChevronDown } from "lucide-react";
import type { ColumnFilterProps, ColumnConfig } from "../types";

export function ColumnFilter<T>({
  column,
  filterType,
  filterOptions,
  theme,
  serverSide = false,
  debounceMs,
  onServerFilterChange,
}: ColumnFilterProps<T>) {
  const [localValue, setLocalValue] = useState<string>("");
  const [selectedValues, setSelectedValues] = useState<string[]>([]); // New state for multi-select
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDebouncing, setIsDebouncing] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const columnFilterValue = serverSide ? localValue : column.getFilterValue();

  const columnConfig = (
    column.columnDef.meta as { columnConfig: ColumnConfig<T> }
  )?.columnConfig;
  const isMultiSelect =
    filterType === "multi-select" ||
    (filterType === "select" && columnConfig?.enableMultiSelect);

  useEffect(() => {
    if (!serverSide) {
      if (isMultiSelect) {
        // Handle multi-select values
        const value = columnFilterValue;
        if (Array.isArray(value)) {
          setSelectedValues(value);
        } else if (value) {
          setSelectedValues([value as string]);
        } else {
          setSelectedValues([]);
        }
      } else {
        setLocalValue((columnFilterValue as string) || "");
      }
    }
  }, [columnFilterValue, serverSide, isMultiSelect]);

  const debouncedServerFilterChange = useCallback(
    (columnId: string, value: string | string[]) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setIsDebouncing(true);

      debounceTimerRef.current = setTimeout(() => {
        if (onServerFilterChange) {
          onServerFilterChange(columnId, value);
        }
        setIsDebouncing(false);
      }, debounceMs);
    },
    [onServerFilterChange, debounceMs]
  );

  const handleValueChange = (value: string) => {
    setLocalValue(value);

    if (serverSide && onServerFilterChange) {
      const columnId = columnConfig?.serverFilterKey || column.id;
      if (
        filterType === "text" ||
        filterType === "number" ||
        filterType === "date"
      ) {
        debouncedServerFilterChange(columnId, value || "");
      } else {
        onServerFilterChange(columnId, value === "all" ? "" : value);
      }
    } else {
      if (
        filterType === "text" ||
        filterType === "date" ||
        filterType === "number"
      ) {
        column.setFilterValue(value || undefined);
      } else column.setFilterValue(value === "all" ? "" : value);
    }
  };

  const updateFilters = useCallback(
    (newSelectedValues: string[]) => {
      setSelectedValues(newSelectedValues);

      if (serverSide && onServerFilterChange) {
        const columnId = columnConfig?.serverFilterKey || column.id;
        onServerFilterChange(
          columnId,
          newSelectedValues.length > 0 ? newSelectedValues : ""
        );
      } else {
        column.setFilterValue(
          newSelectedValues.length > 0 ? newSelectedValues : undefined
        );
      }
    },
    [serverSide, onServerFilterChange, columnConfig, column]
  );

  const handleMultiSelectToggle = (optionValue: string, checked: boolean) => {
    let newSelectedValues: string[];

    if (checked) {
      newSelectedValues = [...selectedValues, optionValue];
    } else {
      newSelectedValues = selectedValues.filter((v) => v !== optionValue);
    }

    updateFilters(newSelectedValues);
  };

  const handleClearAll = () => {
    updateFilters([]);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (isMultiSelect && filterOptions) {
    const displayText =
      selectedValues.length === 0 ? "All" : `${selectedValues.length} selected`;

    return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`h-8 w-full rounded-full ${theme.rowEvenBg} ${theme.rowText} ${theme.buttonSecondaryBorder} justify-between`}
            >
              <span className="text-sm">{displayText}</span>
              <ChevronDown className="h-3 w-3 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className={`w-[200px] rounded-[16px] ${theme.rowEvenBg} max-h-60 overflow-y-auto`}
          >
            {/* All Option */}
            <DropdownMenuCheckboxItem
              checked={selectedValues.length === 0}
              onCheckedChange={(checked) => {
                if (checked) {
                  handleClearAll();
                }
              }}
              className={`rounded-full ${theme.rowText} ${
                theme.buttonSecondaryHover
              } ${
                selectedValues.length === 0
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : ""
              }`}
            >
              All
            </DropdownMenuCheckboxItem>

            {/* Separator */}
            {filterOptions.length > 0 && (
              <DropdownMenuSeparator className="my-1" />
            )}

            {/* Filter Options */}
            {filterOptions.map((option) => {
              const isSelected = selectedValues.includes(String(option.value));
              return (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={isSelected}
                  onCheckedChange={(checked) =>
                    handleMultiSelectToggle(String(option.value), checked)
                  }
                  className={`rounded-full ${theme.rowText} ${
                    theme.buttonSecondaryHover
                  } ${
                    isSelected ? "bg-blue-50 text-blue-600 font-medium" : ""
                  }`}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
    );
  }

  if (filterType === "select" && filterOptions) {
    return (
      <Select value={localValue || "all"} onValueChange={handleValueChange}>
        <SelectTrigger
          className={`h-8 w-full rounded-full ${theme.rowEvenBg} ${theme.rowText} ${theme.buttonSecondaryBorder}`}
        >
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent className={`rounded-[16px] ${theme.rowEvenBg}`}>
          <SelectItem
            value="all"
            className={`rounded-full ${theme.rowText} ${theme.buttonSecondaryHover}`}
          >
            All
          </SelectItem>
          {filterOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={String(option.value)}
              className={`rounded-full ${theme.rowText} ${theme.buttonSecondaryHover}`}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type={
        filterType === "date"
          ? "date"
          : filterType === "number"
          ? "number"
          : "text"
      }
      placeholder={filterType === "date" ? "Select date..." : "Filter..."}
      value={localValue}
      onChange={(e) => {
        handleValueChange(e.target.value);
      }}
      className={`h-8 rounded-full ${theme.rowText} ${theme.rowEvenBg} ${theme.buttonSecondaryBorder}`}
    />
  );
}
